import { Link, useLocation } from "@tanstack/react-router"
import { formatDistanceToNowStrict } from "date-fns"
import {
	IconXmarkOutline18 as CloseIcon,
	IconSquareArrowUpRightOutline18 as ExternalIcon,
	IconArrowRotateClockwiseOutline18 as RefreshIcon,
} from "nucleo-ui-outline-18"

import type { SessionData } from "@productlane/auth"
import { cn } from "@productlane/lib/src/cn"
import {
	Avatar,
	CopyButton,
	IconButton,
	Loader,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { api } from "@/lib/api"
import { copyThreadCommand, parsePageContext } from "./impersonation-context"

type Dock = RouterOutputs["user"]["impersonationDock"]
type HealthStatus = Dock["health"][number]["items"][number]["status"]

const STATUS_DOT: Record<HealthStatus, string> = {
	ok: "bg-green-500",
	warn: "bg-amber-500",
	error: "bg-red-500",
	off: "bg-gray-300 dark:bg-gray-600",
}

const STATUS_LABEL: Record<HealthStatus, string> = {
	ok: "Healthy",
	warn: "Needs attention",
	error: "Broken",
	off: "Not set up",
}

function Group({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<div className="flex min-w-0 flex-col rounded-lg py-1 ring-1 ring-inset ring-gray-200 dark:ring-gray-700/80">
			<div className="px-2.5 pb-0.5 pt-1.5 text-xs font-medium text-tertiary">
				{label}
			</div>
			{children}
		</div>
	)
}

function HealthRow({
	item,
}: {
	item: Dock["health"][number]["items"][number]
}) {
	const row = (
		<div className="flex items-start gap-2 px-2.5 py-1.5">
			<span
				role="img"
				aria-label={STATUS_LABEL[item.status]}
				className={cn(
					"mt-1.5 h-1.5 w-1.5 flex-none rounded-full",
					STATUS_DOT[item.status],
				)}
			/>
			<div className="flex min-w-0 flex-col">
				<span className="text-[13px] font-medium text-primary">
					{item.label}
				</span>
				<span className="truncate text-xs text-tertiary">{item.detail}</span>
			</div>
		</div>
	)
	if (!item.settingsPath) return row
	return (
		<Link
			to={item.settingsPath}
			className="block rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
		>
			{row}
		</Link>
	)
}

function CopyRow({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-center gap-2 px-2.5 py-1">
			<span className="w-24 flex-none text-xs text-tertiary">{label}</span>
			<span className="min-w-0 flex-1 truncate font-mono text-xs text-primary">
				{value}
			</span>
			<CopyButton value={value} className="h-6 w-6" tooltip={`Copy ${label}`} />
		</div>
	)
}

function ExternalRow({ label, href }: { label: string; href: string }) {
	return (
		<a
			href={href}
			target="_blank"
			rel="noreferrer"
			className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-primary hover:bg-gray-100 dark:hover:bg-gray-800"
		>
			<span className="flex-1">{label}</span>
			<ExternalIcon className="h-3.5 w-3.5 flex-none text-tertiary" />
		</a>
	)
}

export function SupportDock({
	session,
	stripeUrl,
	onClose,
}: {
	session: SessionData
	stripeUrl: string | null
	onClose: () => void
}) {
	const location = useLocation()
	const page = parsePageContext(location.pathname)
	const dockQuery = api.user.impersonationDock.useQuery(undefined, {
		staleTime: 60_000,
	})
	const dock = dockQuery.data

	return (
		<div
			role="dialog"
			aria-label="Support dock"
			className="fixed inset-x-0 bottom-10 z-[44] flex h-[22rem] flex-col bg-white shadow-[0_-16px_40px_-12px_rgba(0,0,0,0.12)] ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10"
		>
			<Tabs defaultValue="health" className="flex min-h-0 flex-1 flex-col">
				<div className="flex h-11 flex-none items-center gap-2 border-b border-gray-200 px-3 dark:border-gray-700/80">
					<Avatar
						size="xs"
						roundness="md"
						name={session.workspaceName}
						image={session.workspaceLogoUrl}
					/>
					<span className="mr-2 text-[13px] font-semibold text-primary">
						{session.workspaceName}
					</span>
					<TabsList variant="light">
						<TabsTrigger value="health" variant="light">
							Health
						</TabsTrigger>
						<TabsTrigger value="page" variant="light">
							This page
						</TabsTrigger>
					</TabsList>
					<div className="ml-auto flex items-center gap-1">
						{dock ? (
							<span className="text-xs text-tertiary">
								{dock.openThreads} open threads, {dock.members} members
								{dock.lastThreadAt
									? `, last thread ${formatDistanceToNowStrict(new Date(dock.lastThreadAt), { addSuffix: true })}`
									: ""}
							</span>
						) : null}
						<IconButton
							variant="ghost"
							size="md"
							Icon={RefreshIcon}
							aria-label="Refresh"
							disabled={dockQuery.isFetching}
							onClick={() => void dockQuery.refetch()}
						/>
						<IconButton
							variant="ghost"
							size="md"
							Icon={CloseIcon}
							aria-label="Close dock"
							onClick={onClose}
						/>
					</div>
				</div>

				<TabsContent
					value="health"
					className="min-h-0 flex-1 overflow-y-auto p-3 pb-4"
				>
					{dockQuery.isLoading ? (
						<div className="flex h-full items-center justify-center">
							<Loader />
						</div>
					) : dockQuery.error ? (
						<div className="text-sm text-secondary">
							{dockQuery.error.message}
						</div>
					) : (
						<div className="grid grid-cols-3 gap-3">
							{dock?.health.map((group) => (
								<Group key={group.key} label={group.label}>
									{group.items.map((item) => (
										<HealthRow key={item.key} item={item} />
									))}
								</Group>
							))}
						</div>
					)}
				</TabsContent>

				<TabsContent
					value="page"
					className="min-h-0 flex-1 overflow-y-auto p-3 pb-4"
				>
					<div className="grid grid-cols-2 gap-3">
						<Group label="This page">
							<CopyRow label="Route" value={location.pathname} />
							{page ? <CopyRow label={page.entity} value={page.id} /> : null}
							{page?.entity === "thread" ? (
								<CopyRow
									label="Copy locally"
									value={copyThreadCommand(page.id)}
								/>
							) : null}
						</Group>
						<Group label="Workspace">
							<CopyRow label="Workspace ID" value={session.workspaceId} />
							<CopyRow label="User ID" value={session.userId} />
							<CopyRow label="Email" value={session.email} />
							{stripeUrl ? (
								<ExternalRow label="Open in Stripe" href={stripeUrl} />
							) : null}
						</Group>
					</div>
				</TabsContent>
			</Tabs>
		</div>
	)
}
